import React from "react";
import PrintableSheet from "./PrintableSheet";
import { colors } from "../../config/site";

const INCOME_ROWS = [
  "Primary job (after taxes)",
  "Second job / side income",
  "Government assistance (SNAP, TANF, SSI, etc.)",
  "Child support / other support",
  "Other income",
];

const FIXED_EXPENSES = [
  "Rent",
  "Renter's insurance",
  "Electric (PECO)",
  "Gas (PGW)",
  "Water",
  "Internet / cable",
  "Phone",
  "Car payment / transit pass",
  "Car insurance",
  "Childcare",
  "Loan payments (student, personal)",
  "Subscriptions (streaming, gym, etc.)",
];

const VARIABLE_EXPENSES = [
  "Groceries",
  "Household items / cleaning",
  "Gas / Uber / SEPTA",
  "Medical / prescriptions",
  "Clothing",
  "Personal care (haircuts, etc.)",
  "Eating out / takeout",
  "Entertainment",
  "School supplies / kids' activities",
  "Pet supplies",
  "Other",
];

const SAVINGS_GOALS = [
  "Emergency fund (goal: 1 month of rent)",
  "Security deposit / next move",
  "Down payment savings",
  "Holidays / birthdays",
  "Other savings goal",
];

const Row = ({ label, idx, total = false }) => (
  <div
    className="grid grid-cols-12 gap-2 items-center py-2 px-3 rounded-md"
    style={{ backgroundColor: idx % 2 === 0 ? colors.cream : colors.ivory }}
  >
    <span
      className={`col-span-7 text-sm ${total ? "font-bold" : ""}`}
      style={{ color: colors.charcoal }}
    >
      {label}
    </span>
    <div
      className="col-span-5 h-7 border-b-2 ml-2"
      style={{ borderColor: colors.textMuted + "66" }}
    />
  </div>
);

const SectionHeader = ({ number, title, accent }) => (
  <div className="flex items-center gap-3 mb-3">
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
      style={{ backgroundColor: accent }}
    >
      {number}
    </div>
    <h2 className="text-xl lg:text-2xl font-bold" style={{ color: colors.charcoal }}>
      {title}
    </h2>
  </div>
);

const BudgetWorksheet = () => {
  return (
    <PrintableSheet
      eyebrow="RENTERS · BUDGET WORKSHEET"
      title="Monthly Household Budget Worksheet"
      subtitle="Use this worksheet to plan a realistic monthly budget. The goal: income covers expenses with room left over to save."
      backHref="/resources/renters"
      backLabel="Back to Renter Resources"
      testId="budget-worksheet-page"
    >
      <div className="space-y-8 print:space-y-6">
        {/* Month + name row */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-2"
              style={{ color: colors.sage }}
            >
              Name
            </p>
            <div className="h-9 border-b-2" style={{ borderColor: colors.primary }} />
          </div>
          <div>
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-2"
              style={{ color: colors.sage }}
            >
              Month / Year
            </p>
            <div className="h-9 border-b-2" style={{ borderColor: colors.primary }} />
          </div>
        </div>

        {/* 1 — Income */}
        <section>
          <SectionHeader number="1" title="Monthly Income (after taxes)" accent={colors.primary} />
          <div className="space-y-1">
            {INCOME_ROWS.map((row, i) => (
              <Row key={row} label={row} idx={i} />
            ))}
            <Row label="Total Monthly Income (A)" idx={INCOME_ROWS.length} total />
          </div>
        </section>

        {/* 2 — Fixed expenses */}
        <section>
          <SectionHeader number="2" title="Fixed Expenses" accent={colors.terracotta} />
          <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
            These stay the same each month.
          </p>
          <div className="space-y-1">
            {FIXED_EXPENSES.map((row, i) => (
              <Row key={row} label={row} idx={i} />
            ))}
            <Row label="Total Fixed Expenses (B)" idx={FIXED_EXPENSES.length} total />
          </div>
        </section>

        {/* 3 — Variable expenses */}
        <section>
          <SectionHeader number="3" title="Variable Expenses" accent={colors.sage} />
          <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
            These change month to month. Estimate based on the last 2–3 months.
          </p>
          <div className="space-y-1">
            {VARIABLE_EXPENSES.map((row, i) => (
              <Row key={row} label={row} idx={i} />
            ))}
            <Row label="Total Variable Expenses (C)" idx={VARIABLE_EXPENSES.length} total />
          </div>
        </section>

        {/* 4 — Savings */}
        <section>
          <SectionHeader number="4" title="Savings Goals" accent={colors.gold} />
          <p className="text-sm mb-3" style={{ color: colors.textMuted }}>
            Aim for at least 5–10% of income going toward savings.
          </p>
          <div className="space-y-1">
            {SAVINGS_GOALS.map((row, i) => (
              <Row key={row} label={row} idx={i} />
            ))}
            <Row label="Total Savings (D)" idx={SAVINGS_GOALS.length} total />
          </div>
        </section>

        {/* 5 — The math */}
        <section
          className="rounded-2xl p-6 print:p-4"
          style={{ backgroundColor: colors.primary, color: colors.white }}
        >
          <p
            className="text-xs uppercase tracking-widest font-bold mb-3"
            style={{ color: colors.gold }}
          >
            The Math
          </p>
          <h3 className="text-xl font-bold mb-4">
            (A) Income − (B + C + D) Expenses & Savings = What&apos;s Left
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between border-b border-white/30 pb-2">
                <span>Total Income (A)</span>
                <span className="font-bold">$________</span>
              </div>
              <div className="flex justify-between border-b border-white/30 pb-2">
                <span>Fixed Expenses (B)</span>
                <span className="font-bold">$________</span>
              </div>
              <div className="flex justify-between border-b border-white/30 pb-2">
                <span>Variable Expenses (C)</span>
                <span className="font-bold">$________</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between border-b border-white/30 pb-2">
                <span>Savings (D)</span>
                <span className="font-bold">$________</span>
              </div>
              <div className="flex justify-between border-b border-white/30 pb-2">
                <span>B + C + D Total Out</span>
                <span className="font-bold">$________</span>
              </div>
              <div
                className="flex justify-between pt-2 pb-2 text-base px-2 rounded"
                style={{ backgroundColor: colors.terracotta }}
              >
                <span className="font-bold">Money Left Over</span>
                <span className="font-bold">$________</span>
              </div>
            </div>
          </div>
          <p className="text-xs opacity-90 mt-4">
            If your &ldquo;Money Left Over&rdquo; is negative, look at variable expenses first — those
            are the ones you can adjust. If it&apos;s positive, move it into savings.
          </p>
        </section>

        {/* Tips */}
        <section>
          <h3 className="text-lg font-bold mb-3" style={{ color: colors.charcoal }}>
            Quick Tips
          </h3>
          <ul className="space-y-2 text-sm" style={{ color: colors.charcoal }}>
            <li className="flex gap-3">
              <span
                className="font-bold flex-shrink-0"
                style={{ color: colors.terracotta }}
              >
                •
              </span>
              <span>
                <strong>30% rule:</strong> Aim to spend no more than 30% of your take-home pay on
                rent.
              </span>
            </li>
            <li className="flex gap-3">
              <span
                className="font-bold flex-shrink-0"
                style={{ color: colors.terracotta }}
              >
                •
              </span>
              <span>
                <strong>Pay yourself first:</strong> Move savings to a separate account on payday —
                before you spend.
              </span>
            </li>
            <li className="flex gap-3">
              <span
                className="font-bold flex-shrink-0"
                style={{ color: colors.terracotta }}
              >
                •
              </span>
              <span>
                <strong>Track for 30 days:</strong> Write down every dollar you spend for one month.
                Most people are surprised by where money actually goes.
              </span>
            </li>
            <li className="flex gap-3">
              <span
                className="font-bold flex-shrink-0"
                style={{ color: colors.terracotta }}
              >
                •
              </span>
              <span>
                <strong>Build an emergency fund:</strong> Even $20 a paycheck adds up. Goal: 1 month
                of rent set aside.
              </span>
            </li>
          </ul>
        </section>
      </div>
    </PrintableSheet>
  );
};

export default BudgetWorksheet;
